<template>
  <v-card class="mt-6">
    <v-card-text class="pa-0">
      <div class="px-4 pt-4">
        <v-row dense>
          <v-col cols="12" md="1" sm="6">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('components.rowsPerPage.entries') }}
            </div>
            <v-select
              v-model="filters.itemsPerPage"
              :items="itemsPerPageOptions"
              :disabled="!items.length"
              solo
              hide-details />
          </v-col>
          <v-col cols="12" md="3">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('miscellaneous.search') }}
            </div>
            <v-text-field
              v-model="filters.search"
              :placeholder="$t('miscellaneous.searchByName')"
              prepend-inner-icon="mdi-magnify"
              solo
              hide-details
              clearable />
          </v-col>
          <v-col cols="12" md="2" sm="6">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('entities.stockTransactions.overview.variantStatus') }}
            </div>
            <v-select
              v-model="filters.active"
              :items="variantStatusOptions"
              :disabled="isLoading"
              :label="$t('entities.products.filters.activeVariants')"
              solo
              hide-details="auto" />
          </v-col>
          <v-col cols="12" md="2">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('miscellaneous.startDate') }}
            </div>
            <v-text-field
              v-model="filters.startDate"
              type="date"
              solo
              hide-details
              clearable />
          </v-col>
          <v-col cols="12" md="2">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('miscellaneous.endDate') }}
            </div>
            <v-text-field
              v-model="filters.endDate"
              type="date"
              solo
              hide-details
              clearable />
          </v-col>
          <v-col cols="12" md="2" sm="6">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('entities.stockTransactions.overview.dayTypeLabel') }}
            </div>
            <v-select
              v-model="filters.dayType"
              :items="dayTypeOptions"
              :disabled="isLoading"
              solo
              hide-details />
          </v-col>
          <v-col cols="12" md="2">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('entities.finance.chooseMonth') }}
            </div>
            <div class="d-flex align-center">
              <v-menu
                ref="menuMonthPicker"
                v-model="monthPickerMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="selectedMonth"
                    prepend-icon="mdi-calendar"
                    readonly
                    solo
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    style="flex: 1; max-width: 180px" />
                </template>
                <v-date-picker
                  v-model="selectedMonth"
                  :locale="$i18n.locale"
                  :max="maxDate"
                  type="month"
                  no-title
                  scrollable
                  @input="onMonthSelected" />
              </v-menu>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    depressed
                    x-small
                    fab
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="ml-2"
                    @click="exportToPDF">
                    <v-icon color="white" small>mdi-file-pdf-box</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('entities.finance.generatePdf') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    depressed
                    x-small
                    fab
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="ml-2"
                    @click="exportToCSV">
                    <v-icon color="white" small>mdi-file-excel-box</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('entities.finance.generateCSV') }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
      </div>
      <v-toolbar flat class="px-4 my-4">
        <v-toolbar-title>
          {{ $t('entities.stockTransactions.overview.title') }}
        </v-toolbar-title>
      </v-toolbar>

      <!-- Desktop Table View -->
      <v-simple-table class="mt-4 d-none d-md-block">
        <thead>
          <tr>
            <th>{{ $t('entities.products.entity') }}</th>
            <th>{{ $t('entities.products.variant') }}</th>
            <th class="text-left" style="width: 10%">
              {{ $t('miscellaneous.active') }}
            </th>
            <th class="text-center" style="width: 5%">
              {{ $t('entities.stockTransactions.overview.startingStock') }}
            </th>
            <th class="text-center" style="width: 5%">
              {{ $t('entities.stockTransactions.overview.closingStock') }}
            </th>
            <th class="text-center" style="width: 5%">
              {{ $t('entities.stockTransactions.overview.stockChange') }}
            </th>
            <th class="text-center" style="width: 2%"></th>
            <th class="text-center" style="width: 4%">
              {{ $t('entities.stockTransactions.overview.addedItems') }}
            </th>
            <th class="text-center" style="width: 4%">
              {{ $t('entities.stockTransactions.overview.removedItems') }}
            </th>
            <th class="text-center" style="width: 4%">
              {{ $t('entities.stockTransactions.overview.soldItems') }}
            </th>
            <th class="text-center" style="width: 4%">
              {{ $t('entities.stockTransactions.overview.wastedItems') }}
            </th>
            <th class="text-center" style="width: 18%">
              {{ $t('miscellaneous.actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="11" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" />
            </td>
          </tr>
          <tr v-else-if="!pagedItems.length">
            <td colspan="11" class="text-center grey--text py-6">
              {{ $t('components.noData.noEntriesFound') }}
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="item in pagedItems"
              :key="item.id"
              :class="{ 'deleted-row': !!item.deleted_at }">
              <td>{{ item.arena_product_name || '-' }}</td>
              <td>
                <span
                  :class="{
                    'text-decoration-line-through': !!item.deleted_at,
                  }">
                  {{ item.name || '-' }}
                </span>
                <v-chip
                  v-if="item.deleted_at"
                  color="grey"
                  x-small
                  dark
                  class="ml-2">
                  {{ $t('miscellaneous.deleted') }}
                </v-chip>
              </td>
              <td class="text-left">
                <v-chip :color="item.active ? 'green' : 'red'" small dark pill>
                  {{ $t(`miscellaneous.${item.active ? 'yes' : 'no'}`) }}
                </v-chip>
              </td>
              <td class="text-center">
                {{ addThousandSeparator(item.starting_available_stock) }}
              </td>
              <td class="text-center">
                {{ addThousandSeparator(item.closing_available_stock) }}
              </td>
              <td class="text-center">
                <span :class="getStockChangeClass(item)">
                  {{ formatStockChange(item) }}
                </span>
              </td>
              <td></td>
              <td class="text-center">
                {{ addThousandSeparator(item.total_added) }}
              </td>
              <td class="text-center">
                {{ addThousandSeparator(item.total_removed) }}
              </td>
              <td class="text-center">
                {{ addThousandSeparator(item.total_sold) }}
              </td>
              <td class="text-center">
                {{ addThousandSeparator(item.total_wasted) }}
              </td>
              <td class="text-center">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      depressed
                      x-small
                      fab
                      color="success"
                      :disabled="!!item.deleted_at"
                      v-bind="attrs"
                      v-on="on"
                      class="ml-1"
                      @click="openTransaction(item, 'ADD')">
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('entities.stockTransactions.addStock') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      depressed
                      x-small
                      fab
                      color="warning"
                      :disabled="!!item.deleted_at"
                      v-bind="attrs"
                      v-on="on"
                      class="ml-1"
                      @click="openTransaction(item, 'REMOVE')">
                      <v-icon small>mdi-minus</v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    $t('entities.stockTransactions.removeStock')
                  }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      depressed
                      x-small
                      fab
                      color="error"
                      :disabled="!!item.deleted_at"
                      v-bind="attrs"
                      v-on="on"
                      class="ml-1"
                      @click="openTransaction(item, 'WASTE')">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('entities.stockTransactions.wasteStock') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      depressed
                      x-small
                      fab
                      color="info"
                      :disabled="!!item.deleted_at"
                      v-bind="attrs"
                      v-on="on"
                      class="ml-1"
                      @click="openTransaction(item, 'RESOLVE_WASTE')">
                      <v-icon small>mdi-recycle</v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    $t('entities.stockTransactions.resolveWaste')
                  }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      depressed
                      x-small
                      fab
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      class="ml-1"
                      :disabled="!!item.deleted_at"
                      @click="viewTransactions(item)">
                      <v-icon small>mdi-format-list-bulleted</v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    $t('entities.stockTransactions.viewTransactions')
                  }}</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>

      <!-- Mobile Card View -->
      <div class="d-md-none px-4 pb-4">
        <div v-if="isLoading" class="text-center py-6">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="!pagedItems.length" class="text-center grey--text py-6">
          {{ $t('components.noData.noEntriesFound') }}
        </div>
        <template v-else>
          <v-card
            v-for="item in pagedItems"
            :key="item.id"
            class="mb-3"
            :class="{ 'deleted-card': !!item.deleted_at }"
            outlined>
            <v-card-title class="pb-2">
              <div
                class="d-flex justify-space-between align-start"
                style="width: 100%">
                <div style="flex: 1; min-width: 0">
                  <div class="text-subtitle-1 font-weight-bold text-truncate">
                    {{ item.arena_product_name || '-' }}
                  </div>
                  <div
                    class="text-caption grey--text"
                    :class="{
                      'text-decoration-line-through': !!item.deleted_at,
                    }">
                    {{ item.name || '-' }}
                  </div>
                </div>
                <div class="d-flex align-center ml-2">
                  <v-chip
                    v-if="item.deleted_at"
                    color="grey"
                    x-small
                    dark
                    class="mr-1">
                    {{ $t('miscellaneous.deleted') }}
                  </v-chip>
                  <v-chip
                    :color="item.active ? 'green' : 'red'"
                    small
                    dark
                    pill>
                    {{ $t(`miscellaneous.${item.active ? 'yes' : 'no'}`) }}
                  </v-chip>
                </div>
              </div>
            </v-card-title>
            <v-divider />
            <v-card-text class="pb-2">
              <v-row dense>
                <v-col cols="6" class="py-1">
                  <div class="text-caption grey--text">
                    {{
                      $t('entities.stockTransactions.overview.startingStock')
                    }}
                  </div>
                  <div class="text-body-2 font-weight-medium">
                    {{ addThousandSeparator(item.starting_available_stock) }}
                  </div>
                </v-col>
                <v-col cols="6" class="py-1">
                  <div class="text-caption grey--text">
                    {{ $t('entities.stockTransactions.overview.closingStock') }}
                  </div>
                  <div class="text-body-2 font-weight-medium">
                    {{ addThousandSeparator(item.closing_available_stock) }}
                  </div>
                </v-col>
                <v-col cols="6" class="py-1">
                  <div class="text-caption grey--text">
                    {{ $t('entities.stockTransactions.overview.stockChange') }}
                  </div>
                  <div
                    class="text-body-2 font-weight-bold"
                    :class="getStockChangeClass(item)">
                    {{ formatStockChange(item) }}
                  </div>
                </v-col>
                <v-col cols="6" class="py-1">
                  <div class="text-caption grey--text">
                    {{ $t('entities.stockTransactions.overview.addedItems') }}
                  </div>
                  <div class="text-body-2 font-weight-medium success--text">
                    <v-icon x-small color="success">mdi-arrow-up</v-icon>
                    {{ addThousandSeparator(item.total_added) }}
                  </div>
                </v-col>
                <v-col cols="6" class="py-1">
                  <div class="text-caption grey--text">
                    {{ $t('entities.stockTransactions.overview.removedItems') }}
                  </div>
                  <div class="text-body-2 font-weight-medium">
                    {{ addThousandSeparator(item.total_removed) }}
                  </div>
                </v-col>
                <v-col cols="6" class="py-1">
                  <div class="text-caption grey--text">
                    {{ $t('entities.stockTransactions.overview.soldItems') }}
                  </div>
                  <div class="text-body-2 font-weight-medium">
                    {{ addThousandSeparator(item.total_sold) }}
                  </div>
                </v-col>
                <v-col cols="12" class="py-1">
                  <div class="text-caption grey--text">
                    {{ $t('entities.stockTransactions.overview.wastedItems') }}
                  </div>
                  <div class="text-body-2 font-weight-medium">
                    {{ addThousandSeparator(item.total_wasted) }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-center flex-wrap py-2">
              <v-btn
                depressed
                x-small
                fab
                color="success"
                :disabled="!!item.deleted_at"
                class="ma-1"
                @click="openTransaction(item, 'ADD')">
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
              <v-btn
                depressed
                x-small
                fab
                color="warning"
                :disabled="!!item.deleted_at"
                class="ma-1"
                @click="openTransaction(item, 'REMOVE')">
                <v-icon small>mdi-minus</v-icon>
              </v-btn>
              <v-btn
                depressed
                x-small
                fab
                color="error"
                :disabled="!!item.deleted_at"
                class="ma-1"
                @click="openTransaction(item, 'WASTE')">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
              <v-btn
                depressed
                x-small
                fab
                color="info"
                :disabled="!!item.deleted_at"
                class="ma-1"
                @click="openTransaction(item, 'RESOLVE_WASTE')">
                <v-icon small>mdi-recycle</v-icon>
              </v-btn>
              <v-btn
                depressed
                x-small
                fab
                color="primary"
                class="ma-1"
                @click="viewTransactions(item)">
                <v-icon small>mdi-format-list-bulleted</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </div>
    </v-card-text>
    <v-divider />
    <v-card-actions class="justify-center">
      <div>
        <v-pagination
          v-model="page"
          :length="totalPages"
          :total-visible="5"
          :disabled="!filteredItems.length"
          color="primary" />
        <h5
          v-if="paginationSummary"
          class="card-title font-weight-light mt-2 text-center">
          {{ paginationSummary }}
        </h5>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import thousandSeparator from '@/mixins/thousand-separator'

export default {
  name: 'StockOverviewTable',
  mixins: [thousandSeparator],
  props: {
    items: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    initialStartDate: { type: String, default: null },
    initialEndDate: { type: String, default: null },
    initialDayType: { type: String, default: 'business' },
  },
  data() {
    return {
      filters: {
        itemsPerPage: 25,
        search: '',
        startDate: this.initialStartDate,
        endDate: this.initialEndDate,
        active: null,
        dayType: this.initialDayType,
      },
      page: 1,
      selectedMonth: this.initialStartDate
        ? this.initialStartDate.substring(0, 7)
        : new Date().toISOString().substring(0, 7),
      monthPickerMenu: false,
    }
  },
  computed: {
    itemsPerPageOptions() {
      return [10, 25, 50, 100]
    },
    maxDate() {
      return new Date().toISOString().substring(0, 7)
    },
    variantStatusOptions() {
      return [
        {
          text: this.$t('entities.stockTransactions.overview.anyStatus'),
          value: null,
        },
        {
          text: this.$t('entities.stockTransactions.overview.activeStatus'),
          value: true,
        },
        {
          text: this.$t('entities.stockTransactions.overview.inactiveStatus'),
          value: false,
        },
      ]
    },
    dayTypeOptions() {
      return [
        {
          text: this.$t('entities.finance.dayType.business'),
          value: 'business',
        },
        {
          text: this.$t('entities.finance.dayType.calendar'),
          value: 'calendar',
        },
      ]
    },
    filteredItems() {
      if (!Array.isArray(this.items)) return []
      let result = [...this.items]

      // Filter by search (name)
      if (this.filters.search) {
        const searchLower = this.filters.search.toLowerCase()
        result = result.filter((item) => {
          const variantName = (item.name || '').toLowerCase()
          const productName = (item.arena_product_name || '').toLowerCase()
          return (
            variantName.includes(searchLower) ||
            productName.includes(searchLower)
          )
        })
      }

      // Filter by active status when set (true/false)
      if (this.filters.active !== null && this.filters.active !== undefined) {
        const activeFlag = Boolean(this.filters.active)
        result = result.filter((item) => Boolean(item.active) === activeFlag)
      }

      return result
    },
    totalPages() {
      if (!this.filteredItems.length) return 1
      return Math.max(
        1,
        Math.ceil(this.filteredItems.length / this.filters.itemsPerPage),
      )
    },
    pagedItems() {
      if (!this.filteredItems.length) return []
      const start = (this.page - 1) * this.filters.itemsPerPage
      const end = start + this.filters.itemsPerPage
      return this.filteredItems.slice(start, end)
    },
    paginationSummary() {
      const total = this.filteredItems.length
      if (!total) return ''
      const perPage = this.filters.itemsPerPage
      const currentPage = this.page
      const rawFrom = (currentPage - 1) * perPage + 1
      const rawTo = rawFrom + perPage - 1
      const boundedFrom = Math.min(rawFrom, total)
      const boundedTo = Math.min(rawTo, total)
      const formattedFrom = this.addThousandSeparator(boundedFrom)
      const formattedTo = this.addThousandSeparator(boundedTo)
      const formattedTotal = this.addThousandSeparator(total)
      return `${formattedFrom} - ${formattedTo} ${this.$t(
        'components.pagination.of',
      )} ${formattedTotal}`
    },
  },
  methods: {
    getStockChange(item) {
      return item.closing_available_stock - item.starting_available_stock
    },
    formatStockChange(item) {
      const change = this.getStockChange(item)
      const formatted = this.addThousandSeparator(Math.abs(change))
      if (change > 0) return `+${formatted}`
      if (change < 0) return `-${formatted}`
      return formatted
    },
    getStockChangeClass(item) {
      const change = this.getStockChange(item)
      if (change > 0) return 'success--text'
      if (change < 0) return 'error--text'
      return ''
    },
    emitDateFilterChange() {
      if (this.filters.startDate && this.filters.endDate) {
        // Debounce the emission by 1200ms to allow user to change both dates
        this.dateChangeDebounceTimer = setTimeout(() => {
          this.page = 1
          this.$emit('filter:date-change', {
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
          })
        }, 1200)
      }
    },
    openTransaction(item, transactionType) {
      this.$emit('transaction:open', {
        variantId: item.id,
        transactionType,
      })
    },
    viewTransactions(item) {
      this.$emit('transactions:view', {
        variantId: item.id,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
      })
    },
    onMonthSelected(month) {
      if (!month) {
        this.monthPickerMenu = false
        return
      }

      const [year, monthNum] = month.split('-')
      const yearNum = parseInt(year, 10)
      const monthNumInt = parseInt(monthNum, 10)

      // First day of the selected month
      const startDate = `${year}-${monthNum}-01`

      // Last day of the selected month
      const lastDay = new Date(yearNum, monthNumInt, 0).getDate()
      const endDate = `${year}-${monthNum}-${String(lastDay).padStart(2, '0')}`

      this.filters.startDate = startDate
      this.filters.endDate = endDate

      // Close the menu
      this.monthPickerMenu = false
    },
    exportToCSV() {
      if (!this.filteredItems.length) {
        return
      }

      // CSV Headers
      const headers = [
        this.$t('entities.products.entity'),
        this.$t('entities.products.variant'),
        this.$t('miscellaneous.active'),
        this.$t('entities.stockTransactions.overview.startingStock'),
        this.$t('entities.stockTransactions.overview.closingStock'),
        this.$t('entities.stockTransactions.overview.stockChange'),
        this.$t('entities.stockTransactions.overview.addedItems'),
        this.$t('entities.stockTransactions.overview.removedItems'),
        this.$t('entities.stockTransactions.overview.soldItems'),
        this.$t('entities.stockTransactions.overview.wastedItems'),
      ]

      // CSV Rows
      const rows = this.filteredItems.map((item) => {
        const stockChange = this.getStockChange(item)
        return [
          item.arena_product_name || '-',
          item.name || '-',
          item.active
            ? this.$t('miscellaneous.yes')
            : this.$t('miscellaneous.no'),
          item.starting_available_stock,
          item.closing_available_stock,
          stockChange,
          item.total_added,
          item.total_removed,
          item.total_sold,
          item.total_wasted,
        ]
      })

      // Combine headers and rows
      const escapeCsvCell = (cell) => {
        const value = cell === null || cell === undefined ? '' : String(cell)
        const escaped = value.replace(/"/g, '""')
        return `"${escaped}"`
      }
      // Combine headers and rows
      const csvContent = [
        headers.map(escapeCsvCell).join(','),
        ...rows.map((row) => row.map(escapeCsvCell).join(',')),
      ].join('\n')

      // Download CSV
      const blob = new Blob(['\uFEFF' + csvContent], {
        type: 'text/csv;charset=utf-8;',
      })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `stock-transactions-${this.filters.startDate || 'all'}-${
          this.filters.endDate || 'all'
        }.csv`,
      )
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    exportToPDF() {
      if (!this.filteredItems.length) {
        return
      }

      const title = this.$t('entities.stockTransactions.overview.title')
      const dateRange =
        this.filters.startDate && this.filters.endDate
          ? `${this.filters.startDate} - ${this.filters.endDate}`
          : ''

      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 24px; margin-bottom: 10px; }
            .date-range { font-size: 14px; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            tr:nth-child(even) { background-color: #fafafa; }
            .text-center { text-align: center; }
            .positive { color: green; }
            .negative { color: red; }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          ${dateRange ? `<div class="date-range">${dateRange}</div>` : ''}
          <table>
            <thead>
              <tr>
                <th>${this.$t('entities.products.entity')}</th>
                <th>${this.$t('entities.products.variant')}</th>
                <th>${this.$t('miscellaneous.active')}</th>
                <th class="text-center">${this.$t(
                  'entities.stockTransactions.overview.startingStock',
                )}</th>
                <th class="text-center">${this.$t(
                  'entities.stockTransactions.overview.closingStock',
                )}</th>
                <th class="text-center">${this.$t(
                  'entities.stockTransactions.overview.stockChange',
                )}</th>
                <th class="text-center">${this.$t(
                  'entities.stockTransactions.overview.addedItems',
                )}</th>
                <th class="text-center">${this.$t(
                  'entities.stockTransactions.overview.removedItems',
                )}</th>
                <th class="text-center">${this.$t(
                  'entities.stockTransactions.overview.soldItems',
                )}</th>
                <th class="text-center">${this.$t(
                  'entities.stockTransactions.overview.wastedItems',
                )}</th>
              </tr>
            </thead>
            <tbody>
      `

      this.filteredItems.forEach((item) => {
        const stockChange = this.getStockChange(item)
        const changeClass =
          stockChange > 0 ? 'positive' : stockChange < 0 ? 'negative' : ''
        const changeFormatted = this.formatStockChange(item)

        htmlContent += `
          <tr>
            <td>${item.arena_product_name || '-'}</td>
            <td>${item.name || '-'}</td>
            <td>${
              item.active
                ? this.$t('miscellaneous.yes')
                : this.$t('miscellaneous.no')
            }</td>
            <td class="text-center">${this.addThousandSeparator(
              item.starting_available_stock,
            )}</td>
            <td class="text-center">${this.addThousandSeparator(
              item.closing_available_stock,
            )}</td>
            <td class="text-center ${changeClass}">${changeFormatted}</td>
            <td class="text-center">${this.addThousandSeparator(
              item.total_added,
            )}</td>
            <td class="text-center">${this.addThousandSeparator(
              item.total_removed,
            )}</td>
            <td class="text-center">${this.addThousandSeparator(
              item.total_sold,
            )}</td>
            <td class="text-center">${this.addThousandSeparator(
              item.total_wasted,
            )}</td>
          </tr>
        `
      })

      htmlContent += `
            </tbody>
          </table>
        </body>
        </html>
      `

      // Open in new window for printing/saving as PDF
      const printWindow = window.open('', '_blank')
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 250)
    },
  },
  watch: {
    'filters.search'() {
      this.page = 1
    },
    'filters.itemsPerPage'() {
      this.page = 1
    },
    'filters.startDate'(newVal) {
      if (newVal) {
        this.selectedMonth = newVal.substring(0, 7)
      }
      this.emitDateFilterChange()
    },
    'filters.endDate'() {
      this.emitDateFilterChange()
    },
    'filters.active'() {
      this.page = 1
    },
    'filters.dayType'(newVal) {
      this.page = 1
      this.$emit('filter:day-type-change', newVal)
    },
  },
  beforeDestroy() {
    // Clean up timer when component is destroyed
    if (this.dateChangeDebounceTimer) {
      clearTimeout(this.dateChangeDebounceTimer)
    }
  },
}
</script>

<style scoped>
.deleted-row {
  opacity: 0.55;
}

.deleted-card {
  opacity: 0.55;
}
</style>
